import React, { useContext, useEffect } from 'react';
import useTranslate from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import InputField from '~/components/shared/Input/InputField';
import { RootState } from '~/store';
import {
  FormStatus,
  SimpleEvent,
  ValidationContext,
  ValidationType,
} from '~/components/hoc/withValidation';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';
import { updateForm } from '~/store/slices/cardOffer.slice';
import { Checkbox, Stack } from '@qred/components-library';
import { ECardPurpose } from '~/enums';

export const CardPurpose = () => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const validationContext = useContext(ValidationContext);
  const { validationErrors } = validationContext;
  const otherPurposeReasonTextMaxCharLimit = 200;

  const {
    cardOffer: { form },
  } = useSelector((state: RootState) => state);

  const { applicationReasons } = form;

  const validationErrorShouldBeShown =
    (validationContext.formStatus === FormStatus.SUBMITTED &&
      validationErrors.applicationReasons) ||
    validationErrors.ApplicationReasonOther;

  useEffect(() => {
    const fakeEvent: SimpleEvent = {
      target: {
        name: 'applicationReasons',
        value: applicationReasons,
        dataset: { validationType: 'CardPurpose' },
      },
    };
    validationContext.onChangeHOC(fakeEvent);

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'applicationReasons'
      );
    };
  }, [applicationReasons]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.type === 'checkbox') {
      validationContext.onChangeHOC({
        target: {
          name: 'applicationReasons',
          value: applicationReasons,
          dataset: {
            validationType: 'CardPurpose' as ValidationType,
          },
        },
      });
      if (applicationReasons.find((r) => r.reason === e.target.name)) {
        dispatch(
          updateForm({
            ...form,
            applicationReasons: applicationReasons.filter(
              (r) => r.reason !== e.target.name
            ),
          })
        );
      } else {
        dispatch(
          updateForm({
            ...form,
            applicationReasons: [
              ...applicationReasons,
              {
                reason: e.target.name as ECardPurpose,
                reasonText: null,
              },
            ],
          })
        );
      }
    } else {
      validationContext.onChangeHOC({
        target: {
          name: 'specifyCardPurpose',
          value: e.target.value,
          dataset: {
            validationType: 'ApplicationReasonOther' as ValidationType,
          },
        },
      });
      dispatch(
        updateForm({
          ...form,
          applicationReasons: [
            ...applicationReasons.map((r) => {
              if (r.reason === ECardPurpose.OtherPurpose) {
                return { ...r, reasonText: e.target.value };
              }
              return r;
            }),
          ],
        })
      );
    }
  };

  const otherCardPurposeReason = applicationReasons.find(
    (r) => r.reason === ECardPurpose.OtherPurpose
  )?.reasonText;

  return (
    <Stack spacing={'md'}>
      {Object.values(ECardPurpose).map((option: string, index: number) => (
        <Checkbox
          key={`${option}`}
          checked={applicationReasons.some((r) => r.reason === option)}
          onChange={handleOnChange}
          name={option}
          className={validationErrorShouldBeShown ? 'has-error' : ''}
          label={translate(
            `CreditCardPurposeCard.Option${index + 1}` as string
          )}
        />
      ))}

      {applicationReasons &&
        applicationReasons.some(
          (r) => r.reason === ECardPurpose.OtherPurpose
        ) && (
          <InputField
            label="Specify purpose"
            name="specifyCardPurpose"
            onChange={handleOnChange}
            validationType="ApplicationReasonOther"
            placeholder={
              translate('CreditCardPurposeCard.Option9Specify') as string
            }
            maxLength={otherPurposeReasonTextMaxCharLimit}
            characterCounter
            value={otherCardPurposeReason || ''}
          />
        )}
      {validationErrorShouldBeShown && (
        <ValidationErrorMessage>
          {translate('ValidationErrors.CardPurpose')}
        </ValidationErrorMessage>
      )}
    </Stack>
  );
};
